import clsx from "clsx";
import { useEffect, useState, useRef } from "react";
import Select, { components as selectComponents } from "react-select";
import makeAnimated from "react-select/animated";
import uniqid from "uniqid";

import i18n from "../../lib/i18n";
import theme from "@styles/theme";
import OverflowTooltip from "@components_v2/ellipsis-tooltip/overflow-tooltip";

/**
 * 좁은 컨트롤·메뉴에서 문구가 말줄임될 때 전체 문구를 툴팁으로 보여준다. (DAM-387)
 *
 * 포르투갈어처럼 문구가 길어지는 언어에서 "20 visualizações"가 "20 visua⋯"로 잘리는데,
 * 폭을 넓히면 core.css 가 폭을 고정해 둔 컨테이너(.control-pagination 등)를 넘치므로
 * 말줄임을 유지하고 잘린 경우에만 툴팁으로 전체 문구를 제공한다.
 *
 * react-select v3 의 SingleValue·Option 은 forwardRef 가 아니라 ref 를 직접 받지 못하므로,
 * 안쪽에 span 을 두고 그 span 으로 잘림을 측정한다(OverflowTooltip 이 실제로 잘렸을 때만 노출).
 * 아바타·2줄 레이아웃 같은 커스텀 옵션(children 이 문자열이 아닌 경우)은 기존 렌더링을 그대로 둔다.
 */
const withEllipsisTooltip = (Base, spanStyle) => {
    const WithTooltip = ({ children, ...props }) =>
        typeof children !== "string" ? (
            <Base {...props}>{children}</Base>
        ) : (
            <Base {...props}>
                <OverflowTooltip>
                    <span style={spanStyle}>{children}</span>
                </OverflowTooltip>
            </Base>
        );
    WithTooltip.displayName = `WithEllipsisTooltip(${Base.displayName ?? Base.name})`;
    return WithTooltip;
};

// 부모(.select2-selection__single-value)의 말줄임 설정을 그대로 물려받아,
// isTextWrap(.select2-container-text-wrap → white-space: normal !important) 모드를 깨지 않는다.
const SingleValueWithTooltip = withEllipsisTooltip(selectComponents.SingleValue, {
    display: "block",
    overflow: "hidden",
    textOverflow: "inherit",
    whiteSpace: "inherit",
});

// core.css 가 .select2-selection__option 에 white-space: normal !important 를 걸어 옵션은 줄바꿈된다.
// 그 규칙이 걸리지 않는 안쪽 span 에서 말줄임을 적용한다.
const OptionWithTooltip = withEllipsisTooltip(selectComponents.Option, {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const WithSelect = (props) => {
    const {
        isMulti,
        defaultValue,
        options,
        placeholder,
        disabled,
        hidden,
        size,
        isError,
        isTextWrap,
        hideSelectedOptions,
        closeMenuOnSelect,
        onChange,
        onFocus,
        onBlur,
        noOptionsMessage,
        limit,
        ...otherProps
    } = props;

    const [id] = useState(uniqid());
    const [Components] = useState(() =>
        makeAnimated({ SingleValue: SingleValueWithTooltip, Option: OptionWithTooltip }),
    );
    const selectRef = useRef(null);

    const [defaultValue_, setDefaultValue_] = useState("");
    const [value, setValue] = useState("");

    useEffect(() => {
        if (isMulti) {
            setDefaultValue_(defaultValue);
            setValue(defaultValue);
        } else if (options) {
            setDefaultValue_(options.filter((v) => v.value === defaultValue));
            setValue(options.filter((v) => v.value === defaultValue));
        }
    }, [isMulti, defaultValue, options]);

    // input 요소에 직접 인라인 스타일 적용
    useEffect(() => {
        const applyInputStyle = () => {
            if (selectRef.current) {
                const inputElements = selectRef.current.querySelectorAll("input");
                inputElements.forEach((input) => {
                    input.style.setProperty("color", theme.palette.greySecondary.main, "important");
                });

                const selectedValueElements = selectRef.current.querySelectorAll(".select2-selection__single-value");

                selectedValueElements.forEach((input) => {
                    input.style.setProperty("color", theme.palette.greySecondary.main, "important");
                });
            }
        };

        const timer = setTimeout(applyInputStyle, 100);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div ref={selectRef}>
            <Select
                id={id}
                className={clsx("select2-container", {
                    hidden,
                    "select2-container-sm": size === "sm",
                    "select2-container-text-wrap": isTextWrap,
                    "is-error": isError,
                })}
                classNamePrefix={"select2-selection"}
                components={Components}
                isDisabled={disabled}
                defaultValue={defaultValue_}
                value={value}
                onChange={onChange}
                onFocus={(e) => {
                    onFocus && onFocus(e);
                    e.target.closest(".select2-container").classList.add("select2-focus");
                }}
                onBlur={(e) => {
                    if (onBlur) onBlur(e);
                    e.target.closest(".select2-container").classList.remove("select2-focus");
                }}
                placeholder={placeholder === undefined ? i18n.t(`common::label::Select...`) : placeholder}
                options={options}
                isOptionDisabled={(option) => option.disabled}
                isMulti={isMulti}
                hideSelectedOptions={hideSelectedOptions !== undefined ? hideSelectedOptions : false}
                closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : true}
                noOptionsMessage={() => {
                    return noOptionsMessage ? noOptionsMessage : i18n.t(`common::label::No options`);
                }}
                menuPlacement={"auto"}
                {...otherProps}
                styles={{
                    control: (provided) => ({
                        ...provided,
                        minWidth: 120,
                    }),
                    menu: (provided) => ({
                        ...provided,
                        backgroundColor: theme.palette.background.default,
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        backgroundColor: theme.palette.background.default,
                    }),
                    option: (provided, state) => ({
                        ...provided,
                        color: state.isSelected
                            ? `${theme.palette.primary.main} !important`
                            : theme.palette.greySecondary.main,
                        backgroundColor: state.isSelected ? theme.palette.primary.light : provided.backgroundColor,
                        "&:active": {
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                        "&.select2-selection__option--is-selected": {
                            color: `${theme.palette.primary.main} !important`,
                        },
                        "&:hover": {
                            backgroundColor: theme.palette.greySecondary[50],
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                        "&.select2-selection__option--is-focused": {
                            backgroundColor: theme.palette.greySecondary[50],
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                    }),
                }}
            />
        </div>
    );
};

const withCheckbox = (props) => {
    let { data, innerProps, getValue } = props;
    let selectedOptions = getValue();
    let _id = uniqid();

    return (
        <div {...innerProps}>
            <div className={"form-check mt-1 mb-1 ml-2 "}>
                <label htmlFor={_id} className={"form-check-label"}>
                    <input
                        id={_id}
                        type={"checkbox"}
                        className={"form-check-input"}
                        checked={selectedOptions.filter((v) => v.value === data.value).length > 0}
                        value={data.value}
                        readOnly={true}
                    />
                    <i className={"input-helper"}></i>
                    <span>{data.label}</span>
                </label>
            </div>
        </div>
    );
};

WithSelect.defaultProps = {
    isMulti: false,
};

export default WithSelect;
